import type {
  DownloadCSVOptions,
  MultiTableCSVOptions,
} from '@/utils/csvDownload';

import {
  buildExportRowsFromCsvString,
  buildExportRowsFromRecords,
  buildExportRowsFromReportTables,
} from './buildRebateExportSheet';
import {
  xlsxDownloadFilename,
  xlsxFilenameFromCsvFilename,
} from './rebateExportFilename';
import type { RebateExportRow } from './rebateExportTypes';
import { rebateMultiTableDownloadFilename } from './rebateMultiTableDownloadFilename';
import {
  REBATE_XLSX_MIME_TYPE,
  writeRebateReportWorkbook,
} from './writeRebateReportWorkbook';

function triggerXlsxDownload(bytes: ArrayBuffer, filename: string) {
  const blob = new Blob([bytes], { type: REBATE_XLSX_MIME_TYPE });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function downloadRebateExportRows(
  rows: RebateExportRow[],
  filename: string,
): Promise<void> {
  if (rows.length === 0) {
    return;
  }
  const bytes = await writeRebateReportWorkbook(rows);
  triggerXlsxDownload(bytes, filename);
}

export async function downloadRebateMultiTableXlsx({
  tables,
  filename = 'report',
}: MultiTableCSVOptions): Promise<void> {
  if (!tables || tables.length === 0) {
    return;
  }
  await downloadRebateExportRows(
    buildExportRowsFromReportTables(tables),
    filename.toLowerCase().endsWith('.xlsx')
      ? filename
      : rebateMultiTableDownloadFilename(filename, 'xlsx'),
  );
}

export async function downloadRebateRecordsXlsx(
  options: DownloadCSVOptions,
): Promise<void> {
  const rows = buildExportRowsFromRecords(options);
  if (rows.length === 0) {
    return;
  }
  await downloadRebateExportRows(
    rows,
    xlsxDownloadFilename(options.filename ?? 'table-data'),
  );
}

export async function downloadRebateCsvStringXlsx(
  csv: string,
  csvFilename: string,
): Promise<void> {
  await downloadRebateExportRows(
    buildExportRowsFromCsvString(csv),
    xlsxFilenameFromCsvFilename(csvFilename),
  );
}
